import React, { useEffect, useState } from "react";
import PageHeader from "../../../../components/common/PageHeader";
import { useNavigate, useParams } from "react-router-dom";
import { convertToInquiry, getFollowupList } from "../api/followup.api";
import { VERTICALS } from "../../../../constants/verticals";
import { toast } from "react-toastify";
import "../styles/followup.css";
import ListPagination from "../../../../components/common/ListPagination";
import {
    DEFAULT_PAGE_SIZE,
    parsePaginationResponse,
} from "../../../../components/common/paginationUtils";

type FollowupFilters = {
    source_name: string;
    client_segment: string;
    stage: string;
    owner_name: string;
    assigned_to: string;
    schedule_date: string;
};

const createDefaultFilters = (): FollowupFilters => ({
    source_name: "",
    client_segment: "",
    stage: "",
    owner_name: "",
    assigned_to: "",
    schedule_date: "",
});

const buildFollowupListParams = (
    appliedFilters: FollowupFilters,
    search: string
) => {
    const params: Record<string, string> = {};

    Object.entries(appliedFilters).forEach(([key, value]) => {
        if (value !== null && value !== "") {
            params[key] = value;
        }
    });

    const trimmedSearch = search.trim();
    if (trimmedSearch) {
        params.search = trimmedSearch;
    }

    return params;
};

const FollowupList = () => {
    const navigate = useNavigate();
    const { vertical } = useParams<{ vertical: string }>();

    const verticalConfig = VERTICALS.find(v => v.key === vertical);
    const verticalId = verticalConfig?.id;
    const verticalName = verticalConfig?.label;

    const [list, setList] = useState<any[]>([]);
    const [page, setPage] = useState(1);
    const [totalCount, setTotalCount] = useState(0);
    const [showingRecords, setShowingRecords] = useState("");
    const [pageInfo, setPageInfo] = useState("");
    const [pageSize, setPageSize] = useState(DEFAULT_PAGE_SIZE);
    const [openMenuId, setOpenMenuId] = useState<number | null>(null);
    const [filters, setFilters] = useState<FollowupFilters>(createDefaultFilters);
    const [debouncedFilters, setDebouncedFilters] = useState<FollowupFilters>(
        createDefaultFilters
    );
    const [search, setSearch] = useState("");
    const [debouncedSearch, setDebouncedSearch] = useState("");
    const [menuPosition, setMenuPosition] = useState({ top: 0, left: 0 });

    const toggleMenu = (id: number, event: any) => {
        event.stopPropagation();

        const rect = event.currentTarget.getBoundingClientRect();

        setMenuPosition({
            top: rect.bottom + window.scrollY,
            left: rect.left + window.scrollX,
        });

        setOpenMenuId(prev => (prev === id ? null : id));
    };

    const fetchFollowups = async (pageNo = page, size = pageSize) => {
        try {
            if (!verticalId) return;

            const res = await getFollowupList(pageNo, {
                vertical_id: verticalId,
                page_size: size,
                ...buildFollowupListParams(debouncedFilters, debouncedSearch),
            });

            const pagination = parsePaginationResponse(res.data, {
                resultsKey: "data",
                fallbackPageSize: size,
                fallbackPage: pageNo,
            });

            setList(pagination.results);
            setTotalCount(pagination.totalCount);
            setShowingRecords(pagination.showingRecords);
            setPageInfo(pagination.pageInfo);
        } catch (err) {
            console.error(err);
            toast.error("Failed to load followups");
        }
    };

    useEffect(() => {
        const timer = setTimeout(() => {
            setDebouncedFilters((prev) => {
                if (JSON.stringify(prev) === JSON.stringify(filters)) {
                    return prev;
                }
                setPage(1);
                return filters;
            });
        }, 500);

        return () => clearTimeout(timer);
    }, [filters]);

    useEffect(() => {
        const timer = setTimeout(() => {
            setDebouncedSearch((prev) => {
                if (prev === search) return prev;
                setPage(1);
                return search;
            });
        }, 500);

        return () => clearTimeout(timer);
    }, [search]);

    const handlePageSizeChange = (size: number) => {
        setPageSize(size);
        if (page === 1) {
            fetchFollowups(1, size);
        } else {
            setPage(1);
        }
    };

    useEffect(() => {
        fetchFollowups(page, pageSize);
    }, [page, pageSize, verticalId, debouncedFilters, debouncedSearch]);

    const handleConvertToInquiry = async (id: number) => {
        try {
            if (!verticalId) return;

            await convertToInquiry(id, verticalId);

            toast.success("Converted to Inquiry successfully");

            setOpenMenuId(null);

            fetchFollowups(); // refresh

        } catch (err: any) {
            toast.error(err?.response?.data?.message || "Conversion failed");
        }
    };
    useEffect(() => {
        const handleClickOutside = () => setOpenMenuId(null);
        document.addEventListener("click", handleClickOutside);

        return () => document.removeEventListener("click", handleClickOutside);
    }, []);


    return (
        <>
            <PageHeader
                title={`${verticalName} – Sales Followup`}
                breadcrumbs={[
                    { label: "Home" },
                    { label: "Sales" },
                    { label: verticalName || "" },
                    { label: "Followups" },
                ]}
            />

            <div className="container mt-4">
                <div className="info-card">

                    {/* HEADER */}
                    <div className="d-flex justify-content-between align-items-center mb-3">
                        <h4>{verticalName} – Followup List</h4>

                        <input
                            className="form-control form-control-sm"
                            placeholder="Global search..."
                            value={search}
                            onChange={(e) => setSearch(e.target.value)}
                            style={{ width: 240 }}
                        />
                    </div>

                    {/* TABLE */}
                    <div className="inquiry-table-wrapper table-responsive">
                        <table className="table inquiry-table align-middle">

                            {/* HEADER */}
                            <thead>
                                <tr>
                                    <th style={{ textAlign: "center" }}>Action</th>
                                    <th>Source</th>
                                    <th>Client Segment</th>
                                    <th>Stage</th>
                                    <th>Owner</th>
                                    <th>Assigned To</th>
                                    <th>Created Date</th>
                                    <th>Status</th>
                                </tr>

                                {/* FILTER ROW */}
                                <tr className="inquiry-filter-row">
                                    <th></th>

                                    <th>
                                        <input
                                            className="form-control form-control-sm"
                                            placeholder="Search"
                                            value={filters.source_name}
                                            onChange={(e) =>
                                                setFilters((prev) => ({
                                                    ...prev,
                                                    source_name: e.target.value,
                                                }))
                                            }
                                        />
                                    </th>

                                    <th>
                                        <input
                                            className="form-control form-control-sm"
                                            placeholder="Search"
                                            value={filters.client_segment}
                                            onChange={(e) =>
                                                setFilters((prev) => ({
                                                    ...prev,
                                                    client_segment: e.target.value,
                                                }))
                                            }
                                        />
                                    </th>

                                    <th>
                                        <input
                                            className="form-control form-control-sm"
                                            placeholder="Search"
                                            value={filters.stage}
                                            onChange={(e) =>
                                                setFilters((prev) => ({
                                                    ...prev,
                                                    stage: e.target.value,
                                                }))
                                            }
                                        />
                                    </th>

                                    <th>
                                        <input
                                            className="form-control form-control-sm"
                                            placeholder="Search"
                                            value={filters.owner_name}
                                            onChange={(e) =>
                                                setFilters((prev) => ({
                                                    ...prev,
                                                    owner_name: e.target.value,
                                                }))
                                            }
                                        />
                                    </th>

                                    <th>
                                        <input
                                            className="form-control form-control-sm"
                                            placeholder="Search"
                                            value={filters.assigned_to}
                                            onChange={(e) =>
                                                setFilters((prev) => ({
                                                    ...prev,
                                                    assigned_to: e.target.value,
                                                }))
                                            }
                                        />
                                    </th>

                                    <th>
                                        <input
                                            type="date"
                                            className="form-control form-control-sm"
                                            value={filters.schedule_date}
                                            onChange={(e) =>
                                                setFilters((prev) => ({
                                                    ...prev,
                                                    schedule_date: e.target.value,
                                                }))
                                            }
                                        />
                                    </th>
                                    <th>-</th>
                                </tr>
                            </thead>

                            {/* BODY */}
                            <tbody>
                                {list.length === 0 ? (
                                    <tr>
                                        <td colSpan={8} className="text-center py-4">
                                            No followups found
                                        </td>
                                    </tr>
                                ) : (
                                    list.map((row: any) => (
                                        <tr key={row.id}>

                                            {/* ACTION */}
                                            <td style={{ position: "relative", textAlign: "center" }}>
                                                <button
                                                    className="inquiry-action-btn"
                                                    onClick={(e) => toggleMenu(row.id, e)}
                                                >
                                                    ⋮
                                                </button>

                                            </td>

                                            <td>{row.source_name || "-"}</td>
                                            <td>{row.client_segment_name || "-"}</td>
                                            <td>{row.enquiry_stage_name || "-"}</td>
                                            <td>{row.owner_name || "-"}</td>
                                            <td>{row.assigned_to_name || "-"}</td>

                                            <td style={{ textAlign: "center" }}>
                                                {row.date_created
                                                    ? new Date(row.date_created).toLocaleDateString("en-GB")
                                                    : "-"}
                                            </td>

                                            <td>
                                                <span className="badge bg-success">
                                                    Followup
                                                </span>
                                            </td>

                                        </tr>
                                    ))
                                )}
                            </tbody>
                        </table>
                        {openMenuId && (
                            <div
                                className="inquiry-action-dropdown"
                                style={{
                                    position: "fixed",
                                    top: menuPosition.top,
                                    left: menuPosition.left,
                                }}
                                onClick={(e) => e.stopPropagation()}
                            >
                                <div
                                    className="dropdown-item"
                                    onClick={() => {
                                        navigate(`/${vertical}/followups/${openMenuId}/edit`);
                                    }}
                                >
                                    Edit Followup
                                </div>
                                <div
                                    className="dropdown-item"
                                    onClick={() => handleConvertToInquiry(openMenuId)}
                                >
                                    Convert to Inquiry
                                </div>

                            </div>
                        )}
                    </div>

                    <ListPagination
                        page={page}
                        totalCount={totalCount}
                        pageSize={pageSize}
                        currentCount={list.length}
                        showingRecords={showingRecords}
                        pageInfo={pageInfo}
                        onPageChange={setPage}
                        onPageSizeChange={handlePageSizeChange}
                        className="mt-3"
                    />

                </div>
            </div>
        </>
    );
};

export default FollowupList;